import { SupabaseFileStorage } from './supabaseFileStorage';
import { FileItem } from '../types';

interface CachedData {
  files: FileItem[];
  storageInfo: { used: number; total: number };
  timestamp: number;
}

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

class FileManagerPreloader {
  private cache: Map<string, CachedData> = new Map();
  private loadingPromises: Map<string, Promise<CachedData>> = new Map();

  /**
   * Preload file manager data for a workspace
   */
  async preload(workspaceId: string): Promise<void> {
    // If already loading, wait for that promise
    const existingPromise = this.loadingPromises.get(workspaceId);
    if (existingPromise) {
      await existingPromise;
      return;
    }

    // If cached and fresh, return immediately
    const cached = this.cache.get(workspaceId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return;
    }

    // Start loading
    const loadPromise = this.loadData(workspaceId);
    this.loadingPromises.set(workspaceId, loadPromise);

    try {
      await loadPromise;
    } finally {
      this.loadingPromises.delete(workspaceId);
    }
  }

  /**
   * Load data for a workspace
   */
  private async loadData(workspaceId: string): Promise<CachedData> {
    try {
      // Load files and storage info in parallel
      const [files, storageInfo] = await Promise.all([
        SupabaseFileStorage.getFiles(workspaceId),
        SupabaseFileStorage.getStorageInfo(workspaceId),
      ]);

      const data: CachedData = {
        files,
        storageInfo,
        timestamp: Date.now(),
      };

      this.cache.set(workspaceId, data);
      return data;
    } catch (error) {
      console.error('[FileManagerPreloader] Error loading data:', error);
      // Return empty data on error
      return {
        files: [],
        storageInfo: { used: 0, total: 100 * 1024 * 1024 },
        timestamp: Date.now(),
      };
    }
  }

  /**
   * Get cached data for a workspace
   */
  getCached(workspaceId: string): CachedData | null {
    const cached = this.cache.get(workspaceId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached;
    }
    return null;
  }

  /**
   * Invalidate cache for a workspace
   */
  invalidate(workspaceId: string): void {
    this.cache.delete(workspaceId);
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }
}

export const fileManagerPreloader = new FileManagerPreloader();

